import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@gymflow/helpers";
import { SubscriptionSessionPackDTO } from "@gymflow/types";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import { DateTime } from "luxon";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TabsType } from "../../UserMember/UserSessions/types";
import {
  Button,
  EditIcon,
  HelpCircleIcon,
  InfinityIcon,
  MenuPopover,
  MenuPopoverOptionType,
} from "..";
import CustomTooltip from "../CustomTooltip";
import { CancelIcon } from "../icons/CancelIcon";
import { Coins03Icon } from "../icons/Coins03Icon";
import { Power01Icon } from "../icons/Power01Icon";
import { AdjustQuantityModal } from "./components/AdjustQuantityModal";
import { CreditPackItemStatus } from "./components/CreditPackItemStatus";
import { EditExpiryDateModal } from "./components/EditExpiryDateModal";
import { ExpireCreditPackModal } from "./components/ExpireCreditPackModal";

export type CreditPackItemType = SubscriptionSessionPackDTO;

type CreditPackItemProps = {
  as: "member" | "staff";
  creditPack: CreditPackItemType;
  selectedItemId?: number;
  setSelectedItemId: (item: number) => void;
  activeTabId: TabsType;
  userMemberId?: string;
};

export const CreditPackItem = ({
  as,
  selectedItemId,
  setSelectedItemId,
  activeTabId,
  userMemberId,
  creditPack: {
    id,
    name,
    expiryDate,
    sessionsLeft,
    termsConditions,
    sessionsIncluded,
    sessionsUnlimited,
    partOfMembership,
    status,
    membershipName,
  },
}: CreditPackItemProps) => {
  const { t } = useTranslation();
  const { setModal, hide: hideModal } = useContext(ModalContext);
  const { timezone } = useClubSettings();

  const hideActions = as === "member" || !userMemberId;

  const menuOptions = useMemo(() => {
    if (hideActions) {
      return [];
    }

    const baseMenuOptions = [
      {
        id: "adjustQuantity",
        label: t("components.creditPackItem.menuOptions.adjustQuantity"),
        icon: (
          <Coins03Icon
            className="size-5"
            pathClassName="stroke-gray-500 dark:stroke-gray-400"
          />
        ),
        onClick: () => {
          setModal(
            <AdjustQuantityModal
              hide={() => hideModal()}
              totalCredits={sessionsIncluded}
              remaining={sessionsLeft}
              subscriptionId={id}
              userMemberId={userMemberId}
            />,
          );
        },
      },
      {
        id: "editExpiryDate",
        label: t("components.creditPackItem.menuOptions.editExpiryDate"),
        icon: (
          <EditIcon
            className="size-5"
            pathClassName="stroke-gray-500 dark:stroke-gray-400"
          />
        ),
        onClick: () => {
          setModal(
            <EditExpiryDateModal
              hide={() => hideModal()}
              initialDate={expiryDate}
              subscriptionId={id}
              userMemberId={userMemberId}
            />,
          );
        },
      },
      {
        id: "expire",
        label: t("components.creditPackItem.menuOptions.expire"),
        classNameLabel: "text-error-600",
        icon: <CancelIcon className="size-5" />,
        topDivider: true,
        onClick: () => {
          setModal(
            <ExpireCreditPackModal
              subscriptionId={id}
              userMemberId={userMemberId}
              hide={() => hideModal()}
            />,
          );
        },
      },
    ] satisfies MenuPopoverOptionType[];

    if (status !== "ACTIVE") {
      return [];
    }

    return baseMenuOptions.filter(({ id }) => {
      if (id === "adjustQuantity" && sessionsUnlimited) {
        return false;
      }
      if (id === "editExpiryDate" && partOfMembership) {
        return false;
      }
      if (id === "expire" && partOfMembership) {
        return false;
      }
      return true;
    });
  }, [
    hideActions,
    t,
    status,
    setModal,
    sessionsIncluded,
    sessionsLeft,
    id,
    userMemberId,
    hideModal,
    expiryDate,
    sessionsUnlimited,
    partOfMembership,
  ]);

  return (
    <div
      className={cn(
        "relative min-w-[19rem] flex cursor-pointer flex-row justify-between rounded-2xl border border-gray-200 bg-gray-0 dark:border-gray-600 dark:bg-gray-950 px-4 pb-2 pt-3",
        {
          "ring-secondary-500 ring-2 ring-offset-0": selectedItemId === id,
        },
      )}
      onClick={() => setSelectedItemId(id)}
    >
      {partOfMembership && (
        <div
          className={cn(
            "flex absolute -top-2 right-4 gap-1 justify-center items-center py-0.5 pr-1 pl-2 bg-gray-50 dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-600",
            {
              "ring-secondary-500 ring-2 ring-offset-0": selectedItemId === id,
              "px-2": hideActions,
            },
          )}
        >
          <div className="dark:text-gray-25 text-xs font-medium text-gray-950">
            {t("components.creditPackItem.partOfMembership")}
          </div>
          {!hideActions && (
            <CustomTooltip
              message={t("components.creditPackItem.limitedControls")}
              hideIfEmpty
            >
              <HelpCircleIcon
                className="size-4"
                pathClassName="stroke-gray-500 dark:stroke-gray-400"
              />
            </CustomTooltip>
          )}
        </div>
      )}
      <div className="flex flex-col justify-center overflow-x-hidden">
        <CustomTooltip message={name} asChild>
          <div className="dark:text-gray-25 truncate text-base font-bold text-gray-950">
            {name}
          </div>
        </CustomTooltip>
        <div className="flex items-center gap-1.5">
          <CreditPackItemStatus status={status} />
          {status !== "PENDING" &&
            status !== "CANCELLED" &&
            status !== "EXPIRED" && (
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {expiryDate
                  ? t("components.creditPackItem.expiry", {
                      expiryDate: DateTime.fromISO(expiryDate)
                        .setZone(timezone)
                        .toLocaleString(DateTime.DATE_MED),
                    })
                  : t("components.creditPackItem.noExpiryDate")}
              </div>
            )}
          {status === "EXPIRED" && (
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {expiryDate
                ? t("components.creditPackItem.expired", {
                    expiredDate: DateTime.fromISO(expiryDate)
                      .setZone(timezone)
                      .toLocaleString(DateTime.DATE_MED),
                  })
                : t("components.creditPackItem.noExpiredDate")}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 py-1">
          <a
            className="text-secondary-500 hover:text-secondary-700 active:text-secondary-900 min-w-fit text-xs font-semibold"
            href={termsConditions}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("components.creditPackItem.viewTerms")}
          </a>
          {partOfMembership && membershipName && (
            <div className="flex items-center gap-1.5 overflow-x-hidden">
              <div className="size-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-gray-700" />
              <CustomTooltip message={membershipName} asChild>
                <div className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                  {membershipName}
                </div>
              </CustomTooltip>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-row items-center justify-center gap-3">
        <div className="flex flex-row items-center justify-center gap-1">
          <div className="dark:text-gray-25 text-xl font-bold text-gray-950">
            {sessionsUnlimited ? (
              <InfinityIcon className="dark:stroke-gray-25 stroke-gray-950" />
            ) : (
              sessionsLeft
            )}
          </div>
          <Coins03Icon
            className="size-6"
            pathClassName="stroke-secondary-500"
          />
        </div>
        {activeTabId === "CURRENT" ? (
          <MenuPopover
            classNameContent="w-[14rem]"
            hidden={menuOptions.length === 0}
            triggerButton={
              <Button className="size-9 p-0">
                <FontAwesomeIcon
                  icon={faEllipsisV}
                  className="dark:text-gray-25 size-4 text-gray-950"
                />
              </Button>
            }
            side="bottom"
            options={menuOptions}
          />
        ) : (
          <Button
            className={cn("p-0 w-9 h-9", { hidden: hideActions })}
            hidden={partOfMembership}
            onClick={() => {
              if (hideActions) {
                return;
              }
              setModal(
                <EditExpiryDateModal
                  hide={() => hideModal()}
                  isActivate
                  subscriptionId={id}
                  userMemberId={userMemberId}
                />,
              );
            }}
          >
            <Power01Icon
              className="size-5"
              pathClassName="stroke-gray-950 dark:stroke-gray-25"
            />
          </Button>
        )}
      </div>
    </div>
  );
};
